import { createFileRoute } from '@tanstack/react-router';
import { BrainCircuit, DollarSign, Zap, Shield, BarChart3, Settings, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/ai-gateway')({
  component: AIGatewayPage,
});

const features = [
  {
    iconName: "BrainCircuit",
    title: "Unified Provider Access",
    description: "Single API to access OpenAI, Anthropic, Google, Cohere, and more. Switch providers without code changes.",
  },
  {
    iconName: "DollarSign",
    title: "Cost Tracking",
    description: "Real-time visibility into AI spending by team, project, or application. Set budgets and alerts.",
  },
  {
    iconName: "Zap",
    title: "Smart Routing",
    description: "Route requests to the best model based on cost, latency, or capability requirements.",
  },
  {
    iconName: "Shield",
    title: "Policy Enforcement",
    description: "Control who can use which models, set rate limits, and enforce usage policies.",
  },
  {
    iconName: "BarChart3",
    title: "Analytics & Insights",
    description: "Understand usage patterns, identify optimization opportunities, and track performance.",
  },
  {
    iconName: "Settings",
    title: "Prompt Management",
    description: "Version control for prompts, A/B testing, and centralized prompt library.",
  },
];

const providers = [
  { name: "OpenAI", models: ["GPT-4", "GPT-4 Turbo", "GPT-3.5"] },
  { name: "Anthropic", models: ["Claude 3 Opus", "Claude 3 Sonnet", "Claude 3 Haiku"] },
  { name: "Google", models: ["Gemini Pro", "Gemini Ultra", "PaLM 2"] },
  { name: "Cohere", models: ["Command", "Command Light", "Embed"] },
  { name: "Mistral", models: ["Mistral Large", "Mistral Medium", "Mistral Small"] },
  { name: "Open Source", models: ["Llama 3", "Mixtral", "Phi-3"] },
];

const capabilities = [
  "Automatic failover between providers",
  "Response caching to reduce costs",
  "Token usage tracking per request",
  "Semantic caching for similar queries",
  "Rate limiting per user/team",
  "Content filtering and moderation",
  "Request/response logging",
  "Latency monitoring and alerting",
];

const faqItems = [
  {
    question: "How does the AI Gateway reduce costs?",
    answer: "The gateway optimizes costs through intelligent routing (choosing cheaper models when appropriate), semantic caching (reusing responses for similar queries), and request deduplication. Most organizations see 20-40% cost reduction.",
  },
  {
    question: "Can I use my own API keys?",
    answer: "Yes, you can bring your own API keys for any provider. Matricks securely stores and manages your keys while providing the gateway benefits like cost tracking and policy enforcement.",
  },
  {
    question: "How does failover work?",
    answer: "When a provider experiences issues, the gateway automatically routes requests to a backup provider. You configure fallback priorities and the gateway handles the rest, ensuring high availability.",
  },
  {
    question: "Can I control which models my team uses?",
    answer: "Absolutely. Create policies that restrict access to certain models, set budget limits per team, or require approval for expensive operations. All access is logged for compliance.",
  },
];

function AIGatewayPage() {
  return (
    <div>
      <ContentHeader
        title="AI/LLM Gateway"
        description="Unified access to all major AI providers through a single API. Track costs, enforce policies, and optimize your organization's AI usage."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "AI Gateway" },
        ]}
        badge="Feature"
      />

      {/* Features */}
      <ContentSection
        id="features"
        title="Gateway Features"
        background="muted"
      >
        <FeatureShowcase features={features} columns={3} variant="cards" />
      </ContentSection>

      {/* Providers */}
      <ContentSection
        id="providers"
        title="Supported Providers"
        description="Access all major AI providers through a unified API."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {providers.map((provider) => (
            <div key={provider.name} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{provider.name}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {provider.models.map((model) => (
                  <span
                    key={model}
                    className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs"
                  >
                    {model}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Capabilities */}
      <ContentSection
        id="capabilities"
        title="Enterprise Capabilities"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {capabilities.map((capability) => (
            <div key={capability} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{capability}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Cost Optimization */}
      <ContentSection
        id="cost-optimization"
        title="Cost Optimization"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="font-semibold text-lg mb-4">Save 20-40% on AI Costs</h3>
            <p className="text-muted-foreground leading-relaxed">
              Most organizations overspend on AI because they lack visibility and control.
              The AI Gateway provides the tools to understand and optimize your AI spending.
            </p>
            <ul className="mt-6 space-y-3">
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Route to cheaper models when high capability isn't needed</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Cache responses for frequently asked questions</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Set budgets and prevent cost overruns</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Identify unused or wasteful usage patterns</span>
              </li>
            </ul>
          </div>
          <div className="rounded-xl bg-muted/50 p-6">
            <h3 className="font-semibold mb-4">Cost Dashboard</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>This Month</span>
                <span className="text-2xl font-bold">$2,847</span>
              </div>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div className="h-full w-3/4 bg-primary rounded-full" />
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>75% of $3,800 budget</span>
                <span>$953 remaining</span>
              </div>
              <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span>GPT-4</span>
                  <span>$1,245 (44%)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Claude 3</span>
                  <span>$892 (31%)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Other</span>
                  <span>$710 (25%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Take Control of Your AI Costs"
        description="Start using the AI Gateway today. Track, optimize, and govern all your AI usage in one place."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "View Pricing", href: "/pricing" }}
        background="primary"
      />
    </div>
  );
}
